import { useRef } from 'react'
import { Box, CircularProgress, IconButton, Link, Tooltip, Typography } from '@mui/material'
import { FileText, TriangleAlert, Upload, X } from 'lucide-react'
import type { IngestDetail } from '../../api/types'
import { Mono, Surface } from '../../components/primitives'
import { bytes, listNames, num } from '../../lib/format'
import { tokens } from '../../theme'

/** One file on its way into the region: being read, read, or refused. */
export interface UploadItem {
  key: string
  name: string
  size: number
  state: 'reading' | 'read' | 'failed'
  detail?: IngestDetail
  error?: string
  /** Required fields still without a column. */
  unmapped: string[]
}

interface Props {
  uploads: UploadItem[]
  selected: string | null
  onSelect: (key: string) => void
  onRemove: (key: string) => void
  removing: string | null
  countries: { code: string; name: string }[]
  /** The chosen region's country, which every plot is judged against. */
  regionCountry: string | null
  /** No region chosen yet (or it has no country), so there is nowhere to put a file. */
  disabled?: boolean
  /** Why the drop zone is shut, when it is. */
  disabledReason?: string
  onFiles: (files: File[]) => void
}

/**
 * Step 1 — the files, and the country their plots are judged against.
 *
 * Several at once: a region is often assembled from one file per supplier
 * or cooperative. Each is read on its own, keeps its own column mapping, and
 * can be taken back out (its plots with it) until the run starts.
 */
export function UploadCard({
  uploads, selected, onSelect, onRemove, removing, countries, regionCountry, disabled, disabledReason, onFiles,
}: Props) {
  const input = useRef<HTMLInputElement>(null)
  const pick = () => !disabled && input.current?.click()
  const read = uploads.filter((u) => u.state === 'read')

  // The country is the region's: it is the border every plot is judged
  // against. A file whose CO says otherwise is worth knowing about — those
  // plots will be judged against the region's country all the same — but it
  // is not a reason to refuse it. Suppliers are each plot's own SU; a file
  // may name several, so there is no single one to show here.
  const nameOf = (code: string) => countries.find((c) => c.code === code)?.name ?? code
  const otherCountries = [...new Set(read.flatMap((u) => u.detail?.derived?.countries ?? []))]
    .filter((c) => c !== regionCountry)
  const countryNote = !read.length || !regionCountry || !otherCountries.length
    ? null
    : `${read.length > 1 ? 'The files’' : 'The file’s'} CO names ${listNames(otherCountries.map(nameOf))}; ` +
      `every plot is checked against ${nameOf(regionCountry)}.`

  return (
    <Surface sx={{ p: 5 }}>
      <input
        ref={input}
        type="file"
        multiple
        // No accept filter: GDAL reads some seventy formats, and the API
        // says plainly if it cannot read the one chosen.
        hidden
        data-testid="ingest-file-input"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          if (files.length) onFiles(files)
          e.target.value = ''
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 3.5 }}>
        <Typography variant="h4">Upload</Typography>
        {uploads.length > 0 && !disabled && (
          <Link onClick={pick} sx={{ font: `500 13px ${tokens.font.sans}` }}>Add files</Link>
        )}
      </Box>

      {uploads.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {uploads.map((u) => (
            <FileRow
              key={u.key}
              item={u}
              active={u.key === selected && uploads.length > 1}
              removing={removing === u.key}
              onSelect={() => u.state === 'read' && onSelect(u.key)}
              onRemove={() => onRemove(u.key)}
            />
          ))}
        </Box>
      )}

      <Box
        onClick={pick}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          const files = [...(e.dataTransfer.files ?? [])]
          if (files.length && !disabled) onFiles(files)
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onKeyDown={(e) => !disabled && e.key === 'Enter' && pick()}
        sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          p: uploads.length ? 3.5 : 8,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          border: `1px dashed ${tokens.color.ink300}`, borderRadius: `${tokens.radius.md}px`,
          backgroundColor: tokens.color.ink50, color: 'text.secondary',
          transition: `border-color ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
          '&:hover': { borderColor: disabled ? tokens.color.ink300 : tokens.color.brandTeal },
        }}
      >
        {!uploads.length && <Upload size={20} strokeWidth={1.5} />}
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {disabled
            ? (disabledReason ?? 'Choose a region first')
            : uploads.length ? 'Drop more files here, or choose them' : 'Drop files, or choose them'}
        </Typography>
        {!uploads.length && (
          <Typography variant="caption" sx={{ textAlign: 'center', maxWidth: 340 }}>
            GeoJSON, GeoPackage, KML, File Geodatabase, GML, FlatGeobuf, DXF, CSV with WKT, and
            other GIS formats. Shapefiles and other multi-file formats in a zip. Several at once.
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 3.5 }}>
        <Typography sx={{ font: `500 13px ${tokens.font.sans}`, mb: 1.5 }}>Country</Typography>
        <Box
          sx={{
            height: 36, px: 2.5, display: 'flex', alignItems: 'center',
            borderRadius: `${tokens.radius.md}px`,
            border: `1px solid ${countryNote ? tokens.color.warning : tokens.color.ink200}`,
            backgroundColor: countryNote ? '#fdf6e4' : tokens.color.ink50,
            font: `400 14px ${tokens.font.sans}`,
            color: regionCountry ? 'text.primary' : 'text.disabled',
          }}
        >
          {regionCountry ? nameOf(regionCountry) : 'From the region'}
        </Box>
        <Typography variant="caption" sx={{ color: countryNote ? 'text.primary' : 'text.secondary', mt: 1, display: 'block' }}>
          {countryNote ?? 'From the region. Suppliers are read from each plot’s SU column.'}
        </Typography>
      </Box>
    </Surface>
  )
}

/** A file: its name and size, then what became of it. */
function FileRow({
  item, active, removing, onSelect, onRemove,
}: { item: UploadItem; active: boolean; removing: boolean; onSelect: () => void; onRemove: () => void }) {
  const d = item.detail
  const crs = d?.ingest.detected_crs
  const status =
    item.state === 'reading' ? 'Reading…'
    // The API's message ends with the list of what it reads, which the drop
    // zone already shows; the row keeps to what went wrong.
    : item.state === 'failed' ? (item.error ?? 'Could not be read').replace(/\s*Upload any vector GIS file[\s\S]*$/, '')
    : [
        `${num(d!.ingest.feature_count)} features`,
        d!.read_as?.layer ? `layer “${d!.read_as.layer}” of ${d!.read_as.layer_count}` : null,
        crs === 'EPSG:4326' ? 'WGS-84' : crs ? `reprojected from ${crs}` : null,
        item.unmapped.length ? `map ${item.unmapped.join(', ')}` : 'mapped',
      ].filter(Boolean).join(' · ')
  const tone = item.state === 'failed' ? tokens.color.danger : item.unmapped.length ? tokens.color.warning : null

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex', gap: 3, alignItems: 'center', p: 2.5,
        border: `1px solid ${active ? tokens.color.brandTeal : tokens.color.ink200}`,
        borderRadius: `${tokens.radius.md}px`,
        backgroundColor: active ? tokens.color.brandTeal100 : tokens.color.ink50,
        cursor: item.state === 'read' ? 'pointer' : 'default',
      }}
    >
      <Box
        sx={{
          width: 32, height: 32, flex: 'none', borderRadius: `${tokens.radius.md}px`,
          backgroundColor: item.state === 'failed' ? tokens.color.tintDanger : tokens.color.brandTeal100,
          color: item.state === 'failed' ? tokens.color.danger : tokens.color.brandTeal,
          display: 'grid', placeItems: 'center',
        }}
      >
        {item.state === 'reading'
          ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
          : item.state === 'failed' ? <TriangleAlert size={16} strokeWidth={1.5} /> : <FileText size={16} strokeWidth={1.5} />}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Mono sx={{ display: 'block', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.name}
        </Mono>
        <Typography variant="caption" sx={{ color: tone ?? 'text.secondary', mt: 0.5, display: 'block' }}>
          {bytes(item.size)} · {status}
        </Typography>
      </Box>
      {item.state !== 'reading' && (
        <Tooltip title={item.state === 'read' ? 'Remove this file and its plots' : 'Dismiss'}>
          <span>
            <IconButton
              size="small"
              disabled={removing}
              aria-label={`Remove ${item.name}`}
              onClick={(e) => { e.stopPropagation(); onRemove() }}
            >
              {removing ? <CircularProgress size={14} /> : <X size={14} strokeWidth={1.5} />}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  )
}
